import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { TORSubmitModal } from './TORSubmitModal.jsx';
import { TORReviewModal } from './TORReviewModal.jsx';
import { TORManagerEditModal } from './TORManagerEditModal.jsx';

export function TimeOffView({ currentUser, isManager, initialScope }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  // Default to 'mine' for everyone — the manager review queue is opt-in
  // via the Approve toggle. Reduces "is this me or them" confusion.
  // The Schedule > Time Off Approvals tab overrides to 'queue' so managers
  // land directly on the queue they came to review.
  const [scope, setScope] = useState(initialScope === 'queue' && isManager ? 'queue' : 'mine');
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [pto, setPto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  // CJ 2026-08-03: a pending request a manager cannot approve needs a way
  // back to an approvable state, not just deny.
  const [editModal, setEditModal] = useState(null);
  const [queueFilter, setQueueFilter] = useState('pending');

  async function load() {
    setLoading(true); setError('');
    try {
      const [m, q, p] = await Promise.all([
        api('GET', '/api/tor/me', null, headers),
        isManager ? api('GET', `/api/tor/queue?state=${queueFilter}`, null, headers) : Promise.resolve({ requests: [] }),
        api('GET', '/api/pto/me', null, headers).catch(() => null),
      ]);
      setMine(m.requests || []);
      setQueue(q.requests || []);
      // Keep the top-level available/pending alongside the user row. The
      // old code kept only `p.user`, which is the raw balance — the number
      // that let a second request be submitted against hours a first one
      // had already claimed.
      setPto(p ? { ...(p.user || {}),
                   available_hours: p.available_hours,
                   pending_hours: p.pending_hours } : null);
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [queueFilter]);

  const stateBadge = (s) => {
    const colors = {
      pending:   { bg: '#fff8e1', fg: '#a16207' },
      approved:  { bg: '#e8f5e9', fg: '#2e7d32' },
      denied:    { bg: '#ffebee', fg: '#c62828' },
      cancelled: { bg: '#f5f5f5', fg: '#9e9e9e' },
    };
    const c = colors[s] || colors.cancelled;
    return (
      <span style={{ background: c.bg, color: c.fg, padding: '2px 8px',
                      borderRadius: 999, fontSize: 11, fontWeight: 700,
                      textTransform: 'capitalize' }}>{s}</span>
    );
  };

  function describeRequest(r) {
    const sameDay = r.requested_date_start === r.requested_date_end;
    const dateStr = sameDay ? r.requested_date_start
                            : `${r.requested_date_start} → ${r.requested_date_end}`;
    const partial = r.requested_time_start && r.requested_time_end
                  ? ` · ${r.requested_time_start}–${r.requested_time_end}`
                  : '';
    return dateStr + partial;
  }

  const ptoBalance = Number(pto?.pto_balance_hours || 0);
  const ptoPending = Number(pto?.pending_hours || 0);
  // What can actually be requested: the balance minus hours already spoken
  // for by pending requests. Falls back to the raw balance if an older
  // server hasn't got the field, so this can't render 0 against a real one.
  const ptoAvailable = pto?.available_hours == null
    ? ptoBalance : Number(pto.available_hours);
  const accrualPerHour = Number(pto?.pto_accrual_rate || 0);
  const hoursPerHour = accrualPerHour > 0 ? Math.round(1 / accrualPerHour) : 35;

  return (
    <div className="space-y-3">
      {/* Scope toggle for managers only — clear "Mine" vs "Approve" labels
          per CJ's "is it mine or am I approving" question. */}
      {isManager && (
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'mine', label: 'My time off' },
            { id: 'queue', label: `Approve requests${queue.length ? ` (${queue.length})` : ''}` },
          ].map((m) => (
            <button key={m.id} onClick={() => setScope(m.id)}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                background: scope === m.id ? 'var(--forest)' : '#fff',
                color: scope === m.id ? '#fff' : '#37474f',
                border: `1px solid ${scope === m.id ? 'var(--forest)' : '#d0d4d9'}`,
                borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {m.label}
            </button>
          ))}
        </div>
      )}

      {error && <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-xs text-red-700">{error}</div>
      </div>}

      {scope === 'mine' && (
        <>
          {/* PTO balance card — top of page so staff always know what
              they have before requesting. */}
          {pto && (
            <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                  PTO available to request
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#212121', lineHeight: 1.1, marginTop: 2 }}>
                  {ptoAvailable.toFixed(1)}<span style={{ fontSize: 14, color: '#757575', fontWeight: 500, marginLeft: 4 }}>hrs</span>
                </div>
                {ptoPending > 0 && (
                  <div style={{ fontSize: 11, color: '#a16207', marginTop: 4 }}>
                    {ptoBalance.toFixed(1)} hr balance − {ptoPending.toFixed(1)} hr awaiting approval
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#757575', maxWidth: 360 }}>
                You earn 1 hr of PTO per {hoursPerHour} worked. Balance updates weekly after timecards are approved. When you request time off below, you can opt to use these hours (paid) or take it unpaid.
                {ptoPending > 0 && ' Hours on a request that hasn’t been decided yet are already set aside, so they aren’t available to request twice.'}
              </div>
            </div>
          )}

          <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div className="text-base font-semibold">My Time Off Requests</div>
              <div className="text-xs text-gray-500">
                Submit by Monday two weeks before the affected schedule week to be on-time.
              </div>
            </div>
            <button onClick={() => setShowForm(true)}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
                        background: 'var(--forest)', color: '#fff', border: 'none',
                        borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
              + Request time off
            </button>
          </div>

          {loading && <div className="card text-sm text-gray-500">Loading…</div>}
          {!loading && mine.length === 0 && (
            <div className="card text-sm text-gray-500">No requests yet.</div>
          )}
          {mine.map((r) => (
            <div key={r.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{describeRequest(r)}</div>
                  {stateBadge(r.state)}
                  {r.use_pto ? <span style={{ fontSize: 11, color: '#1565c0' }}>· using {r.pto_hours}h PTO</span> : null}
                </div>
                {r.reason && <div style={{ fontSize: 12, color: '#37474f', marginTop: 2 }}>"{r.reason}"</div>}
                {r.manager_note && <div style={{ fontSize: 11, color: '#757575', marginTop: 2 }}>Manager: "{r.manager_note}"</div>}
                <div style={{ fontSize: 10, color: '#9e9e9e', marginTop: 4 }}>
                  Submitted {r.submission_at?.slice(0, 16)}
                  {r.reviewed_by_name && ` · ${r.state} by ${r.reviewed_by_name} ${r.reviewed_at?.slice(0, 10)}`}
                </div>
              </div>
              {(r.state === 'pending' || r.state === 'approved') && (
                <button onClick={async () => {
                  const isApproved = r.state === 'approved';
                  const refundMsg = (isApproved && r.use_pto && r.pto_hours)
                    ? ` Your ${r.pto_hours}h of PTO will be refunded.`
                    : '';
                  if (!confirm(`Cancel this request?${refundMsg}`)) return;
                  try {
                    await api('PUT', `/api/tor/${r.id}/cancel`, {}, headers);
                    load();
                  } catch (e) { alert(e.message); }
                }} className="btn-secondary" style={{ fontSize: 11 }}>
                  Cancel{r.state === 'approved' ? ' (refunds PTO)' : ''}
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {scope === 'queue' && isManager && (
        <>
          <div className="card">
            <div className="text-base font-semibold">Time Off Requests</div>
            <div className="text-xs text-gray-500">
              Pending requests need approve/deny. Approved requests can be cancelled if plans change — PTO refunds automatically.
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {['pending', 'approved', 'denied', 'cancelled'].map((s) => (
                <button key={s} onClick={() => setQueueFilter(s)}
                  style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600,
                            background: queueFilter === s ? 'var(--ink)' : '#fff',
                            color: queueFilter === s ? '#fff' : 'var(--text-primary)',
                            border: `1px solid ${queueFilter === s ? 'var(--ink)' : 'var(--rule)'}`,
                            borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          {loading && <div className="card text-sm text-gray-500">Loading…</div>}
          {!loading && queue.length === 0 && (
            <div className="card text-sm text-gray-500">Nothing in {queueFilter}.</div>
          )}
          {queue.map((r) => (
            <div key={r.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {r.employee_name} <span style={{ fontSize: 10, color: '#9e9e9e', fontWeight: 500 }}>· {r.employee_role}</span>
                  </div>
                  {r.is_late && <span style={{ background: '#ffebee', color: '#c62828', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>LATE</span>}
                  {r.use_pto ? <span style={{ fontSize: 11, color: '#1565c0' }}>using {r.pto_hours}h PTO</span> : null}
                  {stateBadge(r.state)}
                </div>
                <div style={{ fontSize: 13, color: '#212121', marginTop: 2 }}>{describeRequest(r)}</div>
                {r.reason && <div style={{ fontSize: 12, color: '#37474f', marginTop: 2 }}>"{r.reason}"</div>}
                <div style={{ fontSize: 10, color: '#9e9e9e', marginTop: 4 }}>
                  Submitted {r.submission_at?.slice(0, 16)} · Deadline was {r.deadline_at?.slice(0, 10)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {r.state === 'pending' && (
                  <>
                    <button onClick={() => setReviewModal({ tor: r, action: 'approve' })}
                      style={{ background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6,
                                padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Approve
                    </button>
                    <button onClick={() => setEditModal(r)}
                      style={{ background: '#fff', color: '#37474f', border: '1px solid #d0d4d9',
                                borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Edit
                    </button>
                    <button onClick={() => setReviewModal({ tor: r, action: 'deny' })}
                      style={{ background: '#fff', color: '#c62828', border: '1px solid #ffcdd2',
                                borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Deny
                    </button>
                  </>
                )}
                {r.state === 'approved' && (
                  <button onClick={async () => {
                    const refundMsg = (r.use_pto && r.pto_hours)
                      ? ` ${r.employee_name}'s ${r.pto_hours}h of PTO will be refunded.`
                      : '';
                    if (!confirm(`Cancel ${r.employee_name}'s approved time-off?${refundMsg}`)) return;
                    try {
                      await api('PUT', `/api/tor/${r.id}/cancel`, {}, headers);
                      load();
                    } catch (e) { alert(e.message); }
                  }} className="btn-secondary" style={{ fontSize: 11 }}>
                    Cancel{r.use_pto ? ' (refunds PTO)' : ''}
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {showForm && (
        <TORSubmitModal currentUser={currentUser} ptoBalance={ptoAvailable}
          onClose={() => setShowForm(false)}
          onSubmitted={() => { setShowForm(false); load(); }} />
      )}
      {editModal && (
        <TORManagerEditModal tor={editModal} currentUser={currentUser}
          onClose={() => setEditModal(null)}
          onDone={() => { setEditModal(null); load(); }} />
      )}
      {reviewModal && (
        <TORReviewModal modal={reviewModal} currentUser={currentUser}
          onClose={() => setReviewModal(null)}
          onDone={() => { setReviewModal(null); load(); }} />
      )}
    </div>
  );
}
